import Papa, { ParseResult } from 'papaparse';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import useApiCall from '../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../hooks/useGetGeneralAppData';
import ConsultsService from '../../../../services/ConsultsService';
import cleanString from '../../../../utils/cleanString';
import formatCpf from '../../../../utils/formatCpf';
import hasDesiredKeys from '../../../../utils/hasDesiredKeys';
import hasDuplicates from '../../../../utils/hasDuplicatedValueAtArray';
import is24TimeValid from '../../../../utils/is24TimeValid';
import isCepValid from '../../../../utils/isCepValid';
import isCpfvalid from '../../../../utils/isCpfValid';
import isEmailValid from '../../../../utils/isEmailValid';
import isSingleCoordValid from '../../../../utils/isSingleCordValid';
import removeDuplicates from '../../../../utils/removeDuplicates';
import renderArray from '../../../../utils/renderArrayWithComa';
import toUTF8 from '../../../../utils/toUTF8';
import { CsvBaseDataInterface, CsvMrDataInterface, CsvPreAdmDataInterface, ISendForRouting, ISendForRoutingMr, ImportFileType } from '../types';

export default function useImportConsults() {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImportFile, setSelectedImportFile] = useState<ImportFileType>('preAdm');

  const csvPreAdmFileRef = useRef<HTMLInputElement>(null);
  const [csvPreAdmFileName, setCsvPreAdmFileName] = useState('');
  const [csvPreAdmParsed, setCsvPreAdmParsed] = useState<ISendForRouting[]>([]);

  const csvBaseFileRef = useRef<HTMLInputElement>(null);
  const [csvBaseFileName, setCsvBaseFileName] = useState('');
  const [csvBaseParsed, setCsvBaseParsed] = useState<ISendForRouting[]>([]);

  const csvMrFileRef = useRef<HTMLInputElement>(null);
  const [csvMrFileName, setCsvMrFileName] = useState('');
  const [csvMrParsed, setCsvMrParsed] = useState<ISendForRoutingMr[]>([]);

  const { userProfile, companyCode, userCode, userName } = useGetGeneralAppData();
  const { apiCall } = useApiCall();

  // PRE ADM
  function removeCsvPreAdmFile() {
    setCsvPreAdmFileName('');
    if (csvPreAdmFileRef.current?.value) {
      csvPreAdmFileRef.current.value = '';
    }
  }

  async function handlePreAdmFileUpload(e: ChangeEvent<HTMLInputElement>) {
    const { files } = e.target;
    if (files) {
      toast.dismiss({ containerId: 'preAdmFileUpload' });
      const fileExtension = files[0].name.split('.').pop();
      if (fileExtension !== 'csv') {
        toast.error('Apenas arquivos com a extensão .CSV são permitidos.', { containerId: 'preAdmFileUpload' });
        return;
      }

      setCsvPreAdmFileName(files[0].name);
      const utf8File = await toUTF8(files[0]);

      Papa.parse(utf8File, {
        header: true,
        complete: (results: ParseResult<CsvPreAdmDataInterface>) => {
          const desiredHeaders = ['CPF', 'MATRICULA', 'NOME', 'EMAIL', 'CEP', 'LOGRADOURO', 'NUMERO', 'COMPLEMENTO', 'BAIRRO', 'CIDADE', 'UF', 'LATITUDE', 'LONGITUDE', 'SEM INTEGRA', 'USA FRETADO', 'DIAS TURNO', 'HORARIO DE ENTRADA', 'HORARIO DE SAIDA', 'COD LOCAL TRABALHO'];

          const mappedResultWithCorrectTimes = results.data?.map((x) => {
            const entryTime = x['HORARIO DE ENTRADA'];
            const exitTime = x['HORARIO DE SAIDA'];

            const hasSecondsAtEntryTime = !!entryTime && entryTime?.includes(':') && entryTime?.split(':').length === 3;
            const hasSecondsAtExitTime = !!exitTime && exitTime?.includes(':') && exitTime?.split(':').length === 3;

            const correctEntryTime = hasSecondsAtEntryTime ? entryTime.slice(0, 5) : entryTime;
            const correctExitTime = hasSecondsAtExitTime ? exitTime.slice(0, 5) : exitTime;

            return { ...x, 'HORARIO DE ENTRADA': correctEntryTime, 'HORARIO DE SAIDA': correctExitTime };
          });

          const hasSomeHeaderFromBaseTemplate = results.data.some((index) => !!index['VALOR VT DIA']);

          if (hasSomeHeaderFromBaseTemplate) {
            toast.error('O arquivo contém cabeçalho do modelo de Base, porém o tipo de importação selecionada é do modelo de Pre-Admissão', { containerId: 'preAdmFileUpload' });
            removeCsvPreAdmFile();
            return;
          }

          const filteredArray = mappedResultWithCorrectTimes.filter((index) => !!index['CPF']);
          if (!hasDesiredKeys(filteredArray, desiredHeaders) || filteredArray.length === 0) {
            toast.error('O arquivo não contém os cabeçalhos desejados', { containerId: 'preAdmFileUpload' });
            removeCsvPreAdmFile();
            return;
          }

          const invalidCpfs = filteredArray.filter((index) => !index.CPF || !isCpfvalid(index.CPF));
          const allCpfs = filteredArray.map((index) => index.CPF);
          const hasDuplicatedCpfs = hasDuplicates(allCpfs);
          if (hasDuplicatedCpfs) {
            toast.error('Existem CPF\'s duplicados no arquivo', { containerId: 'preAdmFileUpload' });
            return;
          }

          if (invalidCpfs.length > 0) {
            const mappedCpfsAndLines = invalidCpfs.map((index) => {
              const invalidCpf = index.CPF;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidCpf, line };
            });

            toast.error(`CPF's inválidos: ${renderArray(mappedCpfsAndLines.map((x) => `${x.invalidCpf} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'preAdmFileUpload' });
          }

          const invalidNames = filteredArray.filter((index) => !(index.NOME));
          if (invalidNames.length > 0) {
            const mappedNamesAndLines = invalidNames.map((index) => {
              const invalidName = index.NOME;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidName, line };
            });

            toast.error(`Nomes inválidos: ${renderArray(mappedNamesAndLines.map((x) => `${x.invalidName} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'preAdmFileUpload' });
          }

          const invalidEmails = filteredArray.filter((index) => index.EMAIL && index.EMAIL !== '@' && !(isEmailValid(index.EMAIL)));
          if (invalidEmails.length > 0) {
            const mappedEmailsAndLines = invalidEmails.map((index) => {
              const invalidEmail = index.EMAIL;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidEmail, line };
            });

            toast.error(`E-mails inválidos: ${renderArray(mappedEmailsAndLines.map((x) => `${x.invalidEmail} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'preAdmFileUpload' });
          }

          const invalidCeps = filteredArray.filter((index) => !index.CEP || !(isCepValid(index.CEP)));
          if (invalidCeps.length > 0) {
            const mappedCepsAndLines = invalidCeps.map((index) => {
              const invalidCep = index.CEP;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidCep, line };
            });

            toast.error(`CEP's inválidos: ${renderArray(mappedCepsAndLines.map((x) => `${x.invalidCep} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'preAdmFileUpload' });
          }

          const invalidStreetNames = filteredArray.filter((index) => !(index.LOGRADOURO));
          if (invalidStreetNames.length > 0) {
            const mappedStreetNamesAndLines = invalidStreetNames.map((index) => {
              const invalidStreetName = index.LOGRADOURO;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidStreetName, line };
            });

            toast.error(`Logradouros inválidos: ${renderArray(mappedStreetNamesAndLines.map((x) => `${x.invalidStreetName} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'preAdmFileUpload' });
          }

          const invalidNumbers = filteredArray.filter((index) => (!(index.NUMERO) && index.NUMERO != 0) || isNaN(Number(index.NUMERO)));
          if (invalidNumbers.length > 0) {
            const mappedNumbersAndLines = invalidNumbers.map((index) => {
              const invalidNumber = index.NUMERO;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidNumber, line };
            });

            toast.error(`Números inválidos: ${renderArray(mappedNumbersAndLines.map((x) => `${x.invalidNumber} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'preAdmFileUpload' });
          }

          const invalidDistricts = filteredArray.filter((index) => !(index.BAIRRO));
          if (invalidDistricts.length > 0) {
            const mappedDistrictsAndLines = invalidDistricts.map((index) => {
              const invalidDistrict = index.BAIRRO;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidDistrict, line };
            });

            toast.error(`Bairros inválidos: ${renderArray(mappedDistrictsAndLines.map((x) => `${x.invalidDistrict} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'preAdmFileUpload' });
          }

          const invalidCities = filteredArray.filter((index) => !(index.CIDADE));
          if (invalidCities.length > 0) {
            const mappedCitiesAndLines = invalidCities.map((index) => {
              const invalidCity = index.CIDADE;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidCity, line };
            });

            toast.error(`Cidades inválidas: ${renderArray(mappedCitiesAndLines.map((x) => `${x.invalidCity} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'preAdmFileUpload' });
          }

          const validUfs = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',];
          const invalidUfs = filteredArray.filter((index) => !index.UF || !(validUfs.includes(index.UF)));
          if (invalidUfs.length > 0) {
            const mappedUfsAndLines = invalidUfs.map((index) => {
              const invalidUf = index.UF;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidUf, line };
            });

            toast.error(`UF's inválidos: ${renderArray(mappedUfsAndLines.map((x) => `${x.invalidUf} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'preAdmFileUpload' });
          }

          const invalidLatitudes = filteredArray.filter((index) => index.LATITUDE && !isSingleCoordValid(index.LATITUDE.replaceAll('_', '.').replaceAll(',', '.')));
          if (invalidLatitudes.length > 0) {
            const mappedLatitudesAndLines = invalidLatitudes.map((index) => {
              const invalidLatitude = index.LATITUDE;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidLatitude, line };
            });

            toast.error(`Latitudes inválidas: ${renderArray(mappedLatitudesAndLines.map((x) => `${x.invalidLatitude} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'preAdmFileUpload' });
          }

          const invalidLongitudes = filteredArray.filter((index) => index.LONGITUDE && !isSingleCoordValid(index.LONGITUDE.replaceAll('_', '.').replaceAll(',', '.')));
          if (invalidLongitudes.length > 0) {
            const mappedLongitudesAndLines = invalidLongitudes.map((index) => {
              const invalidLongitude = index.LONGITUDE;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidLongitude, line };
            });

            toast.error(`Longitudes inválidas: ${renderArray(mappedLongitudesAndLines.map((x) => `${x.invalidLongitude} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'preAdmFileUpload' });
          }

          const invalidIntegraRioCard = filteredArray.filter((index) => index['SEM INTEGRA'] != 0 && index['SEM INTEGRA'] != 1);
          if (invalidIntegraRioCard.length > 0) {
            const mappedIntegraRioCardAndLines = invalidIntegraRioCard.map((index) => {
              const invalidIntegraRioCard = index['SEM INTEGRA'];
              const line = filteredArray.indexOf(index) + 2;

              return { invalidIntegraRioCard, line };
            });

            toast.error(`SEM INTEGRA inválidos: ${renderArray(mappedIntegraRioCardAndLines.map((x) => `${x.invalidIntegraRioCard} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'preAdmFileUpload' });
          }

          const invalidUsesAffreightment = filteredArray.filter((index) => index['USA FRETADO'] != 0 && index['USA FRETADO'] != 1);
          if (invalidUsesAffreightment.length > 0) {
            const mappedUsesAffreightmentAndLines = invalidUsesAffreightment.map((index) => {
              const invalidUsesAffreightment = index['USA FRETADO'];
              const line = filteredArray.indexOf(index) + 2;

              return { invalidUsesAffreightment, line };
            });

            toast.error(`USA FRETADO inválidos: ${renderArray(mappedUsesAffreightmentAndLines.map((x) => `${x.invalidUsesAffreightment} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'preAdmFileUpload' });
          }

          const invalidDaysShift = filteredArray.filter((index) => isNaN(Number(index['DIAS TURNO']) || ((Number(index['DIAS TURNO'] < 1)) || (Number(index['DIAS TURNO'] > 30)))));
          if (invalidDaysShift.length > 0) {
            const mappedDaysShiftAndLines = invalidDaysShift.map((index) => {
              const invalidDaysShift = index['DIAS TURNO'];
              const line = filteredArray.indexOf(index) + 2;

              return { invalidDaysShift, line };
            });

            toast.error(`DIAS TURNO inválidos: ${renderArray(mappedDaysShiftAndLines.map((x) => `${x.invalidDaysShift} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'preAdmFileUpload' });
          }

          const invalidEntryTimes = filteredArray.filter((index) => !index['HORARIO DE ENTRADA'] || !is24TimeValid(index['HORARIO DE ENTRADA']));
          if (invalidEntryTimes.length > 0) {
            const mappedEntryTimesAndLines = invalidEntryTimes.map((index) => {
              const invalidEntryTime = index['HORARIO DE ENTRADA'];
              const line = filteredArray.indexOf(index) + 2;

              return { invalidEntryTime, line };
            });

            toast.error(`HORARIO DE ENTRADA inválidos: ${renderArray(mappedEntryTimesAndLines.map((x) => `${x.invalidEntryTime} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'preAdmFileUpload' });
          }

          const invalidExitTimes = filteredArray.filter((index) => !index['HORARIO DE SAIDA'] || !is24TimeValid(index['HORARIO DE SAIDA']));
          if (invalidExitTimes.length > 0) {
            const mappedExitTimesAndLines = invalidExitTimes.map((index) => {
              const invalidExitTime = index['HORARIO DE SAIDA'];
              const line = filteredArray.indexOf(index) + 2;

              return { invalidExitTime, line };
            });

            toast.error(`HORARIO DE SAIDA inválidos: ${renderArray(mappedExitTimesAndLines.map((x) => `${x.invalidExitTime} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'preAdmFileUpload' });
          }

          const invalidWorkplaceCodes = filteredArray.filter((index) => !index['COD LOCAL TRABALHO']);
          if (invalidWorkplaceCodes.length > 0) {
            const mappedWorkplaceCodesAndLines = invalidWorkplaceCodes.map((index) => {
              const invalidWorkplaceCode = index['COD LOCAL TRABALHO'];
              const line = filteredArray.indexOf(index) + 2;

              return { invalidWorkplaceCode, line };
            });

            toast.error(`COD LOCAL TRABALHO inválidos: ${renderArray(mappedWorkplaceCodesAndLines.map((x) => `${x.invalidWorkplaceCode} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'preAdmFileUpload' });
          }

          const isSomethingInvalid = invalidCpfs.length > 0 || invalidNames.length > 0 || invalidEmails.length > 0 || invalidCeps.length > 0 || invalidStreetNames.length > 0 || invalidNumbers.length > 0 || invalidDistricts.length > 0 || invalidCities.length > 0 || invalidLatitudes.length > 0 || invalidLongitudes.length > 0 || invalidIntegraRioCard.length > 0 || invalidUsesAffreightment.length > 0 || invalidDaysShift.length > 0 || invalidEntryTimes.length > 0 || invalidExitTimes.length > 0 || invalidWorkplaceCodes.length > 0 || invalidUfs.length > 0 || hasDuplicatedCpfs;

          if (isSomethingInvalid) {
            removeCsvPreAdmFile();
            return;
          }

          const filteredResults = results.data.filter((index) => !!index['CPF']);

          setCsvPreAdmParsed(filteredResults.map((index) => ({
            // matricula: (index['MATRICULA']?.slice(0, 18)) || '',
            // nome: (index['NOME']?.slice(0, 50)) || '',
            // mail: (index['EMAIL']?.slice(0, 50)) || '',
            // logradouro: (index['LOGRADOURO']?.slice(0, 50)),
            // complemento: (index['COMPLEMENTO']?.slice(0, 20)) || '',
            // bairro: (index['BAIRRO']?.slice(0, 25)),
            // cidade: (index['CIDADE']?.slice(0, 30)),
            matricula: cleanString(index['MATRICULA']?.slice(0, 18)) || '',
            nome: cleanString(index['NOME']?.slice(0, 50)) || '',
            mail: cleanString(index['EMAIL']?.slice(0, 50)) || '',
            logradouro: cleanString(index['LOGRADOURO']?.slice(0, 50)),
            complemento: cleanString(index['COMPLEMENTO']?.slice(0, 20)) || '',
            bairro: cleanString(index['BAIRRO']?.slice(0, 25)),
            cidade: cleanString(index['CIDADE']?.slice(0, 30)),
            codConsulta: 0,
            codFuncionario: 0,
            cpf: cleanString(formatCpf(index['CPF'])),
            cep: cleanString(index['CEP']),
            num: Number(cleanString(index['NUMERO'])),
            uf: cleanString(index['UF']),
            lat: index['LATITUDE'] ? Number(cleanString(index['LATITUDE'].replaceAll('_', '.').replaceAll(',', '.'))) : 0,
            lng: index['LONGITUDE'] ? Number(cleanString(index['LONGITUDE'].replaceAll('_', '.').replaceAll(',', '.'))) : 0,
            salario: index['SEM INTEGRA'] ? 1000 : 5000,
            codEndA: 0,
            horaEntra: cleanString(index['HORARIO DE ENTRADA']),
            horaSai: cleanString(index['HORARIO DE SAIDA']),
            distancia: 0,
            valorVT: 0,
            diasVT: Number(cleanString(index['DIAS TURNO'])),
            usaFretado: Number(index['USA FRETADO']) === 1,
            opcaoVTFreta: Number(index['USA FRETADO']) === 1,
            usaEspelhamento: false,
            usaTeto: false,
            valorTeto: 0,
            semTaxaVT: false,
            semIntegraVT: Number(index['SEM INTEGRA']) === 1,
            compararVT: 0,
            gestaoSaldo: false,
            codEndAlpha: cleanString(index['COD LOCAL TRABALHO']),
            celular: '',
          })));
        },
      });
    }
  }

  const submitPreAdmFile = useCallback(async () => {
    await apiCall({
      apiToCall: ConsultsService.sendForRouting,
      reqBody: JSON.stringify({
        codUsu: userCode,
        perfilUsu: userProfile,
        usuario: userName,
        codEmpresa: companyCode,
        tipoRot: 2,
        servico: 3,
        nomeArquivoImp: csvPreAdmFileName,
        dadosRoteirizar: csvPreAdmParsed.map((x) => ({
          ...x,
          temLinhasBias: false,
          temOpBias: false,
          temLinhasExcecao: false,
          temOpExcecao: false,
          linhasExcecao: null,
          opExcecao: null,
          linhasBias: null,
          opBias: null,
          horaEntra: x.horaEntra.split(':').slice(0, 2).join(':'),
          horaSai: x.horaSai.split(':').slice(0, 2).join(':'),
        })),
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível importar o arquivo (${response.msg})`, { autoClose: false });
          return;
        }
        toast.success('Arquivo importado com sucesso. Visualize as informações através do submenu “Pesquisar consultas”', { autoClose: false, containerId: 'preAdmFileUpload' });
        removeCsvPreAdmFile();
      },
      catchMessage: 'Erro ao importar arquivo. Tente novamente',
    });
  }, [apiCall, companyCode, csvPreAdmFileName, csvPreAdmParsed, userCode, userName, userProfile]);

  // BASE
  function removeCsvBaseFile() {
    setCsvBaseFileName('');
    if (csvBaseFileRef.current?.value) {
      csvBaseFileRef.current.value = '';
    }
  }

  async function handleBaseFileUpload(e: ChangeEvent<HTMLInputElement>) {
    const { files } = e.target;
    toast.dismiss({ containerId: 'baseFileUpload' });
    if (files) {
      const fileExtension = files[0].name.split('.').pop();
      if (fileExtension !== 'csv') {
        toast.error('Apenas arquivos com a extensão .CSV são permitidos.', { containerId: 'baseFileUpload' });
        return;
      }

      setCsvBaseFileName(files[0].name);
      const utf8File = await toUTF8(files[0]);

      Papa.parse(utf8File, {
        header: true,
        complete: (results: ParseResult<CsvBaseDataInterface>) => {
          const desiredHeaders = ['CPF', 'MATRICULA', 'NOME', 'EMAIL', 'CEP', 'LOGRADOURO', 'NUMERO', 'COMPLEMENTO', 'BAIRRO', 'CIDADE', 'UF', 'LATITUDE', 'LONGITUDE', 'SEM INTEGRA', 'USA FRETADO', 'DIAS TURNO', 'HORARIO DE ENTRADA', 'HORARIO DE SAIDA', 'COD LOCAL TRABALHO', 'GESTAO SALDO', 'VALOR VT DIA'];
          const mappedResultWithCorrectTimes = results.data?.map((x) => {
            const entryTime = x['HORARIO DE ENTRADA'];
            const exitTime = x['HORARIO DE SAIDA'];

            const hasSecondsAtEntryTime = !!entryTime && entryTime?.includes(':') && entryTime?.split(':').length === 3;
            const hasSecondsAtExitTime = !!exitTime && exitTime?.includes(':') && exitTime?.split(':').length === 3;

            const correctEntryTime = hasSecondsAtEntryTime ? entryTime.slice(0, 5) : entryTime;
            const correctExitTime = hasSecondsAtExitTime ? exitTime.slice(0, 5) : exitTime;

            return { ...x, 'HORARIO DE ENTRADA': correctEntryTime, 'HORARIO DE SAIDA': correctExitTime };
          });

          const filteredArray = mappedResultWithCorrectTimes.filter((index) => !!index['CPF']);
          if (!hasDesiredKeys(filteredArray, desiredHeaders) || filteredArray.length === 0) {
            toast.error('O arquivo não contém os cabeçalhos necessários', { containerId: 'baseFileUpload' });
            removeCsvBaseFile();
            return;
          }

          const invalidCpfs = filteredArray.filter((index) => !index.CPF || !isCpfvalid(index.CPF));
          const allCpfs = filteredArray.map((index) => index.CPF);
          const hasDuplicatedCpfs = hasDuplicates(allCpfs);
          if (hasDuplicatedCpfs) {
            toast.error('Existem CPF\'s duplicados no arquivo', { containerId: 'baseFileUpload' });
            return;
          }
          if (invalidCpfs.length > 0) {
            const mappedCpfsAndLines = invalidCpfs.map((index) => {
              const invalidCpf = index.CPF;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidCpf, line };
            });

            toast.error(`CPF's inválidos: ${renderArray(mappedCpfsAndLines.map((x) => `${x.invalidCpf} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'baseFileUpload' });
          }

          const invalidNames = filteredArray.filter((index) => !(index.NOME));
          if (invalidNames.length > 0) {
            const mappedNamesAndLines = invalidNames.map((index) => {
              const invalidName = index.NOME;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidName, line };
            });

            toast.error(`Nomes inválidos: ${renderArray(mappedNamesAndLines.map((x) => `${x.invalidName} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'baseFileUpload' });
          }

          const invalidEmails = filteredArray.filter((index) => index.EMAIL && index.EMAIL !== '@' && !(isEmailValid(index.EMAIL)));
          if (invalidEmails.length > 0) {
            const mappedEmailsAndLines = invalidEmails.map((index) => {
              const invalidEmail = index.EMAIL;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidEmail, line };
            });

            toast.error(`E-mails inválidos: ${renderArray(mappedEmailsAndLines.map((x) => `${x.invalidEmail} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'baseFileUpload' });
          }

          const invalidCeps = filteredArray.filter((index) => !index.CEP || !(isCepValid(index.CEP)));
          if (invalidCeps.length > 0) {
            const mappedCepsAndLines = invalidCeps.map((index) => {
              const invalidCep = index.CEP;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidCep, line };
            });

            toast.error(`CEP's inválidos: ${renderArray(mappedCepsAndLines.map((x) => `${x.invalidCep} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'baseFileUpload' });
          }

          const invalidStreetNames = filteredArray.filter((index) => !(index.LOGRADOURO));
          if (invalidStreetNames.length > 0) {
            const mappedStreetNamesAndLines = invalidStreetNames.map((index) => {
              const invalidStreetName = index.LOGRADOURO;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidStreetName, line };
            });

            toast.error(`Logradouros inválidos: ${renderArray(mappedStreetNamesAndLines.map((x) => `${x.invalidStreetName} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'baseFileUpload' });
          }

          const invalidNumbers = filteredArray.filter((index) => (!(index.NUMERO) && index.NUMERO != 0) || isNaN(Number(index.NUMERO)));
          if (invalidNumbers.length > 0) {
            const mappedNumbersAndLines = invalidNumbers.map((index) => {
              const invalidNumber = index.NUMERO;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidNumber, line };
            });

            toast.error(`Números inválidos: ${renderArray(mappedNumbersAndLines.map((x) => `${x.invalidNumber} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'baseFileUpload' });
          }

          const invalidDistricts = filteredArray.filter((index) => !(index.BAIRRO));
          if (invalidDistricts.length > 0) {
            const mappedDistrictsAndLines = invalidDistricts.map((index) => {
              const invalidDistrict = index.BAIRRO;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidDistrict, line };
            });

            toast.error(`Bairros inválidos: ${renderArray(mappedDistrictsAndLines.map((x) => `${x.invalidDistrict} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'baseFileUpload' });
          }

          const invalidCities = filteredArray.filter((index) => !(index.CIDADE));
          if (invalidCities.length > 0) {
            const mappedCitiesAndLines = invalidCities.map((index) => {
              const invalidCity = index.CIDADE;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidCity, line };
            });

            toast.error(`Cidades inválidas: ${renderArray(mappedCitiesAndLines.map((x) => `${x.invalidCity} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'baseFileUpload' });
          }

          const validUfs = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',];
          const invalidUfs = filteredArray.filter((index) => !index.UF || !(validUfs.includes(index.UF)));
          if (invalidUfs.length > 0) {
            const mappedUfsAndLines = invalidUfs.map((index) => {
              const invalidUf = index.UF;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidUf, line };
            });

            toast.error(`UF's inválidos: ${renderArray(mappedUfsAndLines.map((x) => `${x.invalidUf} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'baseFileUpload' });
          }

          const invalidLatitudes = filteredArray.filter((index) => index.LATITUDE && !isSingleCoordValid(index.LATITUDE.replaceAll('_', '.').replaceAll(',', '.')));
          if (invalidLatitudes.length > 0) {
            const mappedLatitudesAndLines = invalidLatitudes.map((index) => {
              const invalidLatitude = index.LATITUDE;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidLatitude, line };
            });

            toast.error(`Latitudes inválidas: ${renderArray(mappedLatitudesAndLines.map((x) => `${x.invalidLatitude} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'baseFileUpload' });
          }

          const invalidLongitudes = filteredArray.filter((index) => index.LONGITUDE && !isSingleCoordValid(index.LONGITUDE.replaceAll('_', '.').replaceAll(',', '.')));
          if (invalidLongitudes.length > 0) {
            const mappedLongitudesAndLines = invalidLongitudes.map((index) => {
              const invalidLongitude = index.LONGITUDE;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidLongitude, line };
            });

            toast.error(`Longitudes inválidas: ${renderArray(mappedLongitudesAndLines.map((x) => `${x.invalidLongitude} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'baseFileUpload' });
          }

          const invalidIntegraRioCard = filteredArray.filter((index) => index['SEM INTEGRA'] != 0 && index['SEM INTEGRA'] != 1);
          if (invalidIntegraRioCard.length > 0) {
            const mappedIntegraRioCardAndLines = invalidIntegraRioCard.map((index) => {
              const invalidIntegraRioCard = index['SEM INTEGRA'];
              const line = filteredArray.indexOf(index) + 2;

              return { invalidIntegraRioCard, line };
            });

            toast.error(`SEM INTEGRA inválidos: ${renderArray(mappedIntegraRioCardAndLines.map((x) => `${x.invalidIntegraRioCard} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'baseFileUpload' });
          }

          const invalidUsesAffreightment = filteredArray.filter((index) => index['USA FRETADO'] != 0 && index['USA FRETADO'] != 1);
          if (invalidUsesAffreightment.length > 0) {
            const mappedUsesAffreightmentAndLines = invalidUsesAffreightment.map((index) => {
              const invalidUsesAffreightment = index['USA FRETADO'];
              const line = filteredArray.indexOf(index) + 2;

              return { invalidUsesAffreightment, line };
            });

            toast.error(`USA FRETADO inválidos: ${renderArray(mappedUsesAffreightmentAndLines.map((x) => `${x.invalidUsesAffreightment} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'baseFileUpload' });
          }

          const invalidDaysShift = filteredArray.filter((index) => isNaN(Number(index['DIAS TURNO']) || ((Number(index['DIAS TURNO'] < 1)) || (Number(index['DIAS TURNO'] > 30)))));
          if (invalidDaysShift.length > 0) {
            const mappedDaysShiftAndLines = invalidDaysShift.map((index) => {
              const invalidDaysShift = index['DIAS TURNO'];
              const line = filteredArray.indexOf(index) + 2;

              return { invalidDaysShift, line };
            });

            toast.error(`DIAS TURNO inválidos: ${renderArray(mappedDaysShiftAndLines.map((x) => `${x.invalidDaysShift} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'baseFileUpload' });
          }

          const invalidEntryTimes = filteredArray.filter((index) => !index['HORARIO DE ENTRADA'] || !is24TimeValid(index['HORARIO DE ENTRADA']));
          if (invalidEntryTimes.length > 0) {
            const mappedEntryTimesAndLines = invalidEntryTimes.map((index) => {
              const invalidEntryTime = index['HORARIO DE ENTRADA'];
              const line = filteredArray.indexOf(index) + 2;

              return { invalidEntryTime, line };
            });

            toast.error(`HORARIO DE ENTRADA inválidos: ${renderArray(mappedEntryTimesAndLines.map((x) => `${x.invalidEntryTime} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'baseFileUpload' });
          }

          const invalidExitTimes = filteredArray.filter((index) => !index['HORARIO DE SAIDA'] || !is24TimeValid(index['HORARIO DE SAIDA']));
          if (invalidExitTimes.length > 0) {
            const mappedExitTimesAndLines = invalidExitTimes.map((index) => {
              const invalidExitTime = index['HORARIO DE SAIDA'];
              const line = filteredArray.indexOf(index) + 2;

              return { invalidExitTime, line };
            });

            toast.error(`HORARIO DE SAIDA inválidos: ${renderArray(mappedExitTimesAndLines.map((x) => `${x.invalidExitTime} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'baseFileUpload' });
          }

          const invalidWorkplaceCodes = filteredArray.filter((index) => !index['COD LOCAL TRABALHO']);
          if (invalidWorkplaceCodes.length > 0) {
            const mappedWorkplaceCodesAndLines = invalidWorkplaceCodes.map((index) => {
              const invalidWorkplaceCode = index['COD LOCAL TRABALHO'];
              const line = filteredArray.indexOf(index) + 2;

              return { invalidWorkplaceCode, line };
            });

            toast.error(`COD LOCAL TRABALHO inválidos: ${renderArray(mappedWorkplaceCodesAndLines.map((x) => `${x.invalidWorkplaceCode} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'baseFileUpload' });
          }

          const invalidGestaoSaldo = filteredArray.filter((index) => index['GESTAO SALDO'] != 0 && index['GESTAO SALDO'] != 1);
          if (invalidGestaoSaldo.length > 0) {
            const mappedGestaoSaldoAndLines = invalidGestaoSaldo.map((index) => {
              const invalidGestaoSaldo = index['GESTAO SALDO'];
              const line = filteredArray.indexOf(index) + 2;

              return { invalidGestaoSaldo, line };
            });

            toast.error(`GESTAO SALDO inválidos: ${renderArray(mappedGestaoSaldoAndLines.map((x) => `${x.invalidGestaoSaldo} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'baseFileUpload' });
          }

          const invalidValorVtDia = filteredArray.filter((index) => isNaN(Number(index['VALOR VT DIA'])));
          if (invalidValorVtDia.length > 0) {
            const mappedValorVtDiaAndLines = invalidValorVtDia.map((index) => {
              const invalidValorVtDia = index['VALOR VT DIA'];
              const line = filteredArray.indexOf(index) + 2;

              return { invalidValorVtDia, line };
            });

            toast.error(`VALOR VT DIA inválidos: ${renderArray(mappedValorVtDiaAndLines.map((x) => `${x.invalidValorVtDia} - Linha ${x.line}`))}`, { autoClose: false, containerId: 'baseFileUpload' });
          }

          const isSomethingInvalid = hasDuplicatedCpfs || invalidCpfs.length > 0 || invalidNames.length > 0 || invalidEmails.length > 0 || invalidCeps.length > 0 || invalidStreetNames.length > 0 || invalidNumbers.length > 0 || invalidDistricts.length > 0 || invalidCities.length > 0 || invalidLatitudes.length > 0 || invalidLongitudes.length > 0 || invalidIntegraRioCard.length > 0 || invalidUsesAffreightment.length > 0 || invalidDaysShift.length > 0 || invalidEntryTimes.length > 0 || invalidExitTimes.length > 0 || invalidWorkplaceCodes.length > 0 || invalidUfs.length > 0 || invalidValorVtDia.length > 0 || invalidGestaoSaldo.length > 0;

          if (isSomethingInvalid) {
            removeCsvBaseFile();
            return;
          }

          const filteredResults = results.data.filter((index) => !!index['CPF']);

          setCsvBaseParsed(filteredResults.map((index) => ({
            matricula: cleanString(index['MATRICULA']?.slice(0, 18)) || '',
            nome: cleanString(index['NOME']?.slice(0, 50)) || '',
            mail: cleanString(index['EMAIL']?.slice(0, 50)) || '',
            logradouro: cleanString(index['LOGRADOURO']?.slice(0, 50)),
            complemento: cleanString(index['COMPLEMENTO']?.slice(0, 20)) || '',
            bairro: cleanString(index['BAIRRO']?.slice(0, 25)),
            cidade: cleanString(index['CIDADE']?.slice(0, 30)),
            codConsulta: 0,
            codFuncionario: 0,
            cpf: cleanString(formatCpf(index['CPF'])),
            cep: cleanString(index['CEP']),
            num: Number(cleanString(index['NUMERO'])),
            uf: cleanString(index['UF']),
            lat: index['LATITUDE'] ? Number(cleanString(index['LATITUDE'].replaceAll('_', '.').replaceAll(',', '.'))) : 0,
            lng: index['LONGITUDE'] ? Number(cleanString(index['LONGITUDE'].replaceAll('_', '.').replaceAll(',', '.'))) : 0,
            salario: index['SEM INTEGRA'] ? 1000 : 5000,
            codEndA: 0,
            horaEntra: cleanString(index['HORARIO DE ENTRADA']),
            horaSai: cleanString(index['HORARIO DE SAIDA']),
            distancia: 0,
            valorVT: Number(cleanString(index['VALOR VT DIA'])),
            diasVT: Number(cleanString(index['DIAS TURNO'])),
            usaFretado: Number(index['USA FRETADO']) === 1,
            opcaoVTFreta: Number(index['USA FRETADO']) === 1,
            usaEspelhamento: false,
            usaTeto: false,
            valorTeto: 0,
            semTaxaVT: false,
            semIntegraVT: Number(index['SEM INTEGRA']) === 1,
            compararVT: 1,
            gestaoSaldo: index['GESTAO SALDO'] === 1,
            codEndAlpha: cleanString(index['COD LOCAL TRABALHO']),
            celular: '',
          })));
        },
      });
    }
  }

  const submitBaseFile = useCallback(async () => {
    // const downloadJSON = (data: any, filename = 'requestBody.json') => {
    //   const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    //   const link = document.createElement('a');
    //   link.href = URL.createObjectURL(blob);
    //   link.download = filename;
    //   document.body.appendChild(link);
    //   link.click();
    //   document.body.removeChild(link);
    // };

    // Chama a função para baixar o JSON
    // downloadJSON({
    //   codUsu: userCode,
    //   perfilUsu: userProfile,
    //   usuario: userName,
    //   codEmpresa: companyCode,
    //   tipoRot: 4,
    //   servico: 3,
    //   nomeArquivoImp: csvBaseFileName,
    //   dadosRoteirizar: csvBaseParsed,
    // });
    await apiCall({
      apiToCall: ConsultsService.sendForRouting,
      reqBody: JSON.stringify({
        codUsu: userCode,
        perfilUsu: userProfile,
        usuario: userName,
        codEmpresa: companyCode,
        tipoRot: 4,
        servico: 3,
        nomeArquivoImp: csvBaseFileName,
        dadosRoteirizar: csvBaseParsed.map((x) => ({
          ...x,
          temLinhasBias: false,
          temOpBias: false,
          temLinhasExcecao: false,
          temOpExcecao: false,
          linhasExcecao: null,
          opExcecao: null,
          linhasBias: null,
          opBias: null,
          horaEntra: x.horaEntra.split(':').slice(0, 2).join(':'),
          horaSai: x.horaSai.split(':').slice(0, 2).join(':'),
        })),
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível importar o arquivo (${response.msg})`, { autoClose: false });
          return;
        }
        toast.success('Arquivo importado com sucesso. Visualize as informações através do submenu “Pesquisar consultas”', { autoClose: false, containerId: 'baseFileUpload' });
        removeCsvBaseFile();
      },
      catchMessage: 'Erro ao importar arquivo. Tente novamente',
    });
  }, [apiCall, companyCode, csvBaseFileName, csvBaseParsed, userCode, userName, userProfile]);

  // MULTI ROTAS
  function removeCsvMrFile() {
    setCsvMrFileName('');
    if (csvMrFileRef.current?.value) {
      csvMrFileRef.current.value = '';
    }
  }

  async function handleMrFileUpload(e: ChangeEvent<HTMLInputElement>) {
    const { files } = e.target;

    if (files) {
      const fileExtension = files[0].name.split('.').pop();
      toast.dismiss({ containerId: 'mrFileUpload' });
      if (fileExtension !== 'csv') {
        toast.error('Apenas arquivos com a extensão .CSV são permitidos.', { containerId: 'mrFileUpload' });
        return;
      }

      setCsvMrFileName(files[0].name);
      const utf8File = await toUTF8(files[0]);

      Papa.parse(utf8File, {
        header: true,
        complete: (results: ParseResult<CsvMrDataInterface>) => {
          const desiredHeaders = ['CPF', 'MATRICULA', 'NOME', 'EMAIL', 'CEP', 'LOGRADOURO', 'NUMERO', 'COMPLEMENTO', 'BAIRRO', 'CIDADE', 'UF', 'NOME ROTA', 'COD LOCAL TRABALHO 1', 'COD LOCAL TRABALHO 2', 'COD LOCAL TRABALHO 3', 'COD LOCAL TRABALHO 4', 'COD LOCAL TRABALHO 5', 'COD LOCAL TRABALHO 6', 'COD LOCAL TRABALHO 7'];
          const filteredArray = results.data.filter((index) => !!index['CPF']);
          if (!hasDesiredKeys(filteredArray, desiredHeaders) || filteredArray.length === 0) {
            toast.error('O arquivo não contém os cabeçalhos necessários', { containerId: 'mrFileUpload' });
            removeCsvMrFile();
            return;
          }

          const invalidCpfs = filteredArray.filter((index) => !index.CPF || !isCpfvalid(index.CPF));
          const allCpfs = filteredArray.map((index) => index.CPF);
          const hasDuplicatedCpfs = hasDuplicates(allCpfs);
          if (hasDuplicatedCpfs) {
            toast.error('Existem CPF\'s duplicados no arquivo', { containerId: 'mrFileUpload' });
            return;
          }
          if (invalidCpfs.length > 0) {
            const mappedCpfsAndLines = invalidCpfs.map((index) => {
              const invalidCpf = index.CPF;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidCpf, line };
            });

            toast.error(`CPF's inválidos: ${renderArray(mappedCpfsAndLines.map((x) => `${x.invalidCpf} - Linha ${x.line}`))}`, { containerId: 'mrFileUpload', autoClose: false });
            removeCsvMrFile();
          }

          const invalidNames = filteredArray.filter((index) => !(index.NOME));
          if (invalidNames.length > 0) {
            const mappedNamesAndLines = invalidNames.map((index) => {
              const invalidName = index.NOME;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidName, line };
            });

            toast.error(`Nomes inválidos: ${renderArray(mappedNamesAndLines.map((x) => `${x.invalidName} - Linha ${x.line}`))}`, { containerId: 'mrFileUpload', autoClose: false });
          }

          const invalidEmails = filteredArray.filter((index) => index.EMAIL && index.EMAIL !== '@' && !(isEmailValid(index.EMAIL)));
          if (invalidEmails.length > 0) {
            const mappedEmailsAndLines = invalidEmails.map((index) => {
              const invalidEmail = index.EMAIL;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidEmail, line };
            });

            toast.error(`E-mails inválidos: ${renderArray(mappedEmailsAndLines.map((x) => `${x.invalidEmail} - Linha ${x.line}`))}`, { containerId: 'mrFileUpload', autoClose: false });
          }

          const invalidCeps = filteredArray.filter((index) => !index.CEP || !(isCepValid(index.CEP)));
          if (invalidCeps.length > 0) {
            const mappedCepsAndLines = invalidCeps.map((index) => {
              const invalidCep = index.CEP;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidCep, line };
            });

            toast.error(`CEP's inválidos: ${renderArray(mappedCepsAndLines.map((x) => `${x.invalidCep} - Linha ${x.line}`))}`, { containerId: 'mrFileUpload', autoClose: false });
          }

          const invalidStreetNames = filteredArray.filter((index) => !(index.LOGRADOURO));
          if (invalidStreetNames.length > 0) {
            const mappedStreetNamesAndLines = invalidStreetNames.map((index) => {
              const invalidStreetName = index.LOGRADOURO;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidStreetName, line };
            });

            toast.error(`Logradouros inválidos: ${renderArray(mappedStreetNamesAndLines.map((x) => `${x.invalidStreetName} - Linha ${x.line}`))}`, { containerId: 'mrFileUpload', autoClose: false });
          }

          const invalidNumbers = filteredArray.filter((index) => (!(index.NUMERO) && index.NUMERO != 0) || isNaN(Number(index.NUMERO)));
          if (invalidNumbers.length > 0) {
            const mappedNumbersAndLines = invalidNumbers.map((index) => {
              const invalidNumber = index.NUMERO;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidNumber, line };
            });

            toast.error(`Números inválidos: ${renderArray(mappedNumbersAndLines.map((x) => `${x.invalidNumber} - Linha ${x.line}`))}`, { containerId: 'mrFileUpload', autoClose: false });
          }

          const invalidDistricts = filteredArray.filter((index) => !(index.BAIRRO));
          if (invalidDistricts.length > 0) {
            const mappedDistrictsAndLines = invalidDistricts.map((index) => {
              const invalidDistrict = index.BAIRRO;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidDistrict, line };
            });

            toast.error(`Bairros inválidos: ${renderArray(mappedDistrictsAndLines.map((x) => `${x.invalidDistrict} - Linha ${x.line}`))}`, { containerId: 'mrFileUpload', autoClose: false });
          }

          const invalidCities = filteredArray.filter((index) => !(index.CIDADE));
          if (invalidCities.length > 0) {
            const mappedCitiesAndLines = invalidCities.map((index) => {
              const invalidCity = index.CIDADE;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidCity, line };
            });

            toast.error(`Cidades inválidas: ${renderArray(mappedCitiesAndLines.map((x) => `${x.invalidCity} - Linha ${x.line}`))}`, { containerId: 'mrFileUpload', autoClose: false });
          }

          const validUfs = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',];
          const invalidUfs = filteredArray.filter((index) => !index.UF || !(validUfs.includes(index.UF)));
          if (invalidUfs.length > 0) {
            const mappedUfsAndLines = invalidUfs.map((index) => {
              const invalidUf = index.UF;
              const line = filteredArray.indexOf(index) + 2;

              return { invalidUf, line };
            });

            toast.error(`UF's inválidos: ${renderArray(mappedUfsAndLines.map((x) => `${x.invalidUf} - Linha ${x.line}`))}`, { containerId: 'mrFileUpload', autoClose: false });
          }

          const invalidFirstWorkplaceCodes = filteredArray.filter((index) => !index['COD LOCAL TRABALHO 1']);
          if (invalidFirstWorkplaceCodes.length > 0) {
            const mappedWorkplaceCodesAndLines = invalidFirstWorkplaceCodes.map((index) => {
              const invalidWorkplaceCode = index['COD LOCAL TRABALHO 1'];
              const line = filteredArray.indexOf(index) + 2;

              return { invalidWorkplaceCode, line };
            });

            toast.error(`COD LOCAL TRABALHO inválidos: ${renderArray(mappedWorkplaceCodesAndLines.map((x) => `${x.invalidWorkplaceCode} - Linha ${x.line}`))}`, { containerId: 'mrFileUpload', autoClose: false });
          }

          const invalidSecondWorkplaceCodes = filteredArray.filter((index) => !index['COD LOCAL TRABALHO 2']);
          if (invalidSecondWorkplaceCodes.length > 0) {
            const mappedWorkplaceCodesAndLines = invalidSecondWorkplaceCodes.map((index) => {
              const invalidWorkplaceCode = index['COD LOCAL TRABALHO 2'];
              const line = filteredArray.indexOf(index) + 2;

              return { invalidWorkplaceCode, line };
            });

            toast.error(`COD LOCAL TRABALHO inválidos: ${renderArray(mappedWorkplaceCodesAndLines.map((x) => `${x.invalidWorkplaceCode} - Linha ${x.line}`))}`, { containerId: 'mrFileUpload', autoClose: false });
          }

          const isSomethingInvalid = hasDuplicatedCpfs || invalidCpfs.length > 0 || invalidNames.length > 0 || invalidEmails.length > 0 || invalidCeps.length > 0 || invalidStreetNames.length > 0 || invalidNumbers.length > 0 || invalidDistricts.length > 0 || invalidCities.length > 0 || invalidFirstWorkplaceCodes.length > 0 || invalidSecondWorkplaceCodes.length > 0 || invalidUfs.length > 0;

          if (isSomethingInvalid) {
            removeCsvMrFile();
            return;
          }

          const filteredResults = filteredArray.filter((index) => !!index['CPF']);

          setCsvMrParsed(filteredResults.map((x, index) => ({
            rotaRelacionada: 0,
            codConsulta: 0,
            codFuncionario: 0,
            codEndA: x['COD LOCAL TRABALHO 1'],
            codEndAlpha: String(cleanString(x['COD LOCAL TRABALHO 1'])).trim(),
            cpf: cleanString(formatCpf(x['CPF'])).trim(),
            matricula: cleanString(x['MATRICULA']?.slice(0, 18)).trim() || '',
            nome: cleanString(x['NOME']?.slice(0, 50)).trim() || '',
            mail: cleanString(x['EMAIL']?.slice(0, 50)).trim() || '',
            cep: cleanString(x['CEP']).trim(),
            logradouro: cleanString(x['LOGRADOURO']?.slice(0, 50)).trim(),
            num: Number(x['NUMERO']),
            complemento: cleanString(x['COMPLEMENTO']?.slice(0, 20)).trim() || '',
            bairro: cleanString(x['BAIRRO']?.slice(0, 25)).trim(),
            cidade: cleanString(x['CIDADE']?.slice(0, 30)).trim(),
            uf: x['UF'],
            lat: 0,
            lng: 0,
            salario: 1000,
            horaEntra: '08:00',
            horaSai: '18:00',
            diasVT: 1,
            semTaxaVT: false,
            semIntegraVT: false,
            gestaoSaldo: false,
            celular: '',
            compararVT: 0,
            distancia: 1000,
            reprocessaTodos: false,
            rotasMR: [],
            rotasMRArq: [
              {
                codConsulta: 0,
                codRota: index + 1,
                identificacaoRota: cleanString(x['NOME ROTA']).trim(),
                sequenciaTrajeto: 0,
                tipoRotaMR: 0,
                codEndLocTrab1: String(cleanString(x['COD LOCAL TRABALHO 1'])).trim() || '',
                codEndLocTrab2: String(cleanString(x['COD LOCAL TRABALHO 2'])).trim() || '',
                codEndLocTrab3: String(cleanString(x['COD LOCAL TRABALHO 3'])).trim() || '',
                codEndLocTrab4: String(cleanString(x['COD LOCAL TRABALHO 4'])).trim() || '',
                codEndLocTrab5: String(cleanString(x['COD LOCAL TRABALHO 5'])).trim() || '',
                codEndLocTrab6: String(cleanString(x['COD LOCAL TRABALHO 6'])).trim() || '',
                codEndLocTrab7: String(cleanString(x['COD LOCAL TRABALHO 7'])).trim() || ''
              }
            ],
          })));
        },
      });
    }
  }

  const submitMrFile = useCallback(async () => {
    await apiCall({
      apiToCall: ConsultsService.sendForMultiRouting,
      reqBody: JSON.stringify({
        codUsu: userCode,
        perfilUsu: userProfile,
        usuario: userName,
        codEmpresa: companyCode,
        tipoRot: 6,
        servico: 2,
        nomeArquivoImp: csvMrFileName,
        dadosRoteirizarMR: csvMrParsed.map((x) => ({
          ...x,
          horaEntra: x.horaEntra.split(':').slice(0, 2).join(':'),
          horaSai: x.horaSai.split(':').slice(0, 2).join(':'),
        })),
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          const hasErrorsFromWorkplacesCode = !!response.listaCodErro && response.listaCodErro.length > 0;
          let codesWithErrorsString = '';

          if (hasErrorsFromWorkplacesCode) {
            codesWithErrorsString = renderArray(removeDuplicates(response.listaCodErro.map((x: { codErroLocTrab: string }) => x.codErroLocTrab))) || '';
          }

          toast.error(`Não foi possível importar o arquivo (${response.msg}${hasErrorsFromWorkplacesCode ? ` -> Códigos de local de trabalho inválidos: ${codesWithErrorsString}` : ''})`, { autoClose: false });
          return;
        }
        toast.success('Arquivo importado com sucesso. Visualize as informações através do submenu “Pesquisar consultas”', { autoClose: false, containerId: 'mrFileUpload' });
        removeCsvMrFile();
      },
      catchMessage: 'Erro ao importar arquivo. Tente novamente',
    });
  }, [apiCall, companyCode, csvMrFileName, csvMrParsed, userCode, userName, userProfile]);

  useEffect(() => {
    return () => {
      toast.dismiss({ containerId: 'preAdmFileUpload' });
      toast.dismiss({ containerId: 'baseFileUpload' });
      toast.dismiss({ containerId: 'mrFileUpload' });
    };
  }, []);

  return {
    csvPreAdmParsed,
    userProfile,
    isLoading,
    setIsLoading,
    companyCode,
    selectedImportFile,
    setSelectedImportFile,
    csvPreAdmFileName,
    handlePreAdmFileUpload,
    submitPreAdmFile,
    csvPreAdmFileRef,
    csvBaseFileRef,
    csvBaseFileName,
    handleBaseFileUpload,
    submitBaseFile,
    csvMrFileName,
    handleMrFileUpload,
    csvMrFileRef,
    submitMrFile,
  };
}
